// apps/site-dna-web/src/components/ev/ChargerConfig.js
/**
 * ChargerConfig - Manages charger configuration
 *
 * Displays list of ChargerCards with add/remove functionality.
 * Shows capacity warnings if total exceeds site capacity.
 */

import React from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
} from '@mui/material';
import { Add as AddIcon, EvStation as ChargerIcon } from '@mui/icons-material';
import ChargerCard from './ChargerCard';
import { vortxColors, SectionCard } from '@vortx/shared-ui';

/**
 * ChargerConfig Component
 *
 * @param {Object} props
 * @param {Array} props.chargers - Array of charger configurations
 * @param {Array} props.chargerDatabase - Available chargers from API
 * @param {number} props.siteCapacity - Available site capacity in kW
 * @param {Function} props.onChange - Callback with updated chargers array
 */
function ChargerConfig({
    chargers = [],
    chargerDatabase = [],
    siteCapacity = null,
    onChange,
}) {
    const handleAddCharger = () => {
        const newCharger = {
            id: `charger_${Date.now()}`,
            type: 'L2',
            quantity: 4,
            power_kw: 19.2,
            cost_per_unit: 2500,
        };
        onChange([...chargers, newCharger]);
    };

    const handleUpdateCharger = (index, updatedCharger) => {
        const newChargers = [...chargers];
        newChargers[index] = updatedCharger;
        onChange(newChargers);
    };

    const handleRemoveCharger = (index) => {
        const newChargers = chargers.filter((_, i) => i !== index);
        onChange(newChargers);
    };

    // Calculate totals
    const totalChargers = chargers.reduce((sum, c) => sum + (c.quantity || 0), 0);
    const totalCapacity = chargers.reduce((sum, c) => sum + ((c.quantity || 0) * (c.power_kw || 0)), 0);
    const totalCost = chargers.reduce((sum, c) => sum + ((c.quantity || 0) * (c.cost_per_unit || 0)), 0);

    // Check if over capacity
    const isOverCapacity = siteCapacity && totalCapacity > siteCapacity;

    return (
        <SectionCard
            title="Chargers"
            icon={<ChargerIcon />}
            action={
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddCharger}
                    sx={{
                        borderColor: vortxColors.gold,
                        color: vortxColors.gold,
                        '&:hover': { borderColor: vortxColors.gold, bgcolor: 'rgba(178,140,80,0.1)' },
                    }}
                >
                    Add Charger
                </Button>
            }
        >
            {/* Capacity Info */}
            {isOverCapacity && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Total charger capacity ({Math.round(totalCapacity)} kW) exceeds site capacity ({Math.round(siteCapacity)} kW).
                    The demand-managed and cost-optimized strategies will automatically limit peak demand to stay within site capacity.
                </Alert>
            )}

            {/* Summary */}
            {chargers.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{totalChargers}</strong> chargers • <strong>{Math.round(totalCapacity).toLocaleString()}</strong> kW total • <strong>${totalCost.toLocaleString()}</strong> equipment cost
                    </Typography>
                    {siteCapacity && (
                        <Typography variant="caption" color={isOverCapacity ? 'warning.main' : 'text.secondary'}>
                            Using {Math.round((totalCapacity / siteCapacity) * 100)}% of site capacity
                        </Typography>
                    )}
                </Box>
            )}

            {/* Charger Cards */}
            {chargers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ChargerIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary" gutterBottom>
                        No chargers configured
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddCharger}
                    >
                        Add First Charger
                    </Button>
                </Box>
            ) : (
                chargers.map((charger, index) => (
                    <ChargerCard
                        key={charger.id || index}
                        index={index}
                        charger={charger}
                        chargerDatabase={chargerDatabase}
                        onChange={(updated) => handleUpdateCharger(index, updated)}
                        onRemove={() => handleRemoveCharger(index)}
                    />
                ))
            )}
        </SectionCard>
    );
}

export default ChargerConfig;
